/**
 * Struck / tuned-percussion recipes: kalimba, glass bell (celesta), twinkle, glass tick (sky);
 * handpan, zills (finger cymbals), wood tick (dunes). All additive sines with independent
 * per-partial decays — soft, round and alias-free.
 */
import { perc } from '../env';
import { clamp, mtof, nyquist, semis } from '../util';
import type { Voice } from '../voice';
import { link, noiseSrc, type NoteOpts, type Synth } from './synth';

interface Part {
  /** Frequency ratio. */
  readonly r: number;
  /** Relative amplitude. */
  readonly a: number;
  /** Relative decay length. */
  readonly d: number;
}

interface StrikeOpts {
  readonly attack: number;
  readonly lp: number;
  /** Start this ratio sharp and settle onto pitch (membrane / tongue "pitch drop"). */
  readonly drop?: number;
  readonly dropTime?: number;
}

/** Additive core: sine partials with exponential decays into a gentle low-pass. */
function strike(
  s: Synth,
  t: number,
  f: number,
  peak: number,
  decay: number,
  parts: readonly Part[],
  o: NoteOpts,
  so: StrikeOpts,
): { v: Voice; lp: BiquadFilterNode } {
  const ny = nyquist(s.kit.ctx);
  const v = s.pool.voice(t, s.route, o);
  const lp = v.filter('lowpass', Math.min(so.lp, ny), 0.5);
  lp.connect(v.out);
  for (const p of parts) {
    const pf = f * p.r;
    if (pf >= ny || pf <= 0) continue;
    const osc = v.osc('sine', pf);
    const fr = osc.frequency;
    if (so.drop) {
      fr.setValueAtTime(Math.min(ny, pf * so.drop), t);
      fr.exponentialRampToValueAtTime(pf, t + (so.dropTime ?? 0.06));
    }
    if (o.bend) {
      fr.setValueAtTime(pf, t + 0.09);
      fr.exponentialRampToValueAtTime(Math.min(ny, pf * semis(o.bend)), t + 0.55);
    }
    const g = v.gain();
    link(osc, g, lp);
    const end = perc(g.gain, t, peak * p.a, so.attack, decay * p.d);
    v.play(osc, t, end);
  }
  return { v, lp };
}

/** Short filtered-noise transient (tine click, mallet contact, hand slap). */
function transient(
  v: Voice,
  s: Synth,
  t: number,
  type: BiquadFilterType,
  freq: number,
  q: number,
  peak: number,
  dur: number,
  buf: AudioBuffer = s.kit.noise.white,
): void {
  const src = noiseSrc(v, s.kit, buf, t, t + dur + 0.03);
  const f = v.filter(type, Math.min(freq, nyquist(s.kit.ctx)), q);
  const g = v.gain();
  link(src, f, g, v.out);
  perc(g.gain, t, peak, 0.0006, dur);
}

const KALIMBA: readonly Part[] = [
  { r: 1, a: 1, d: 1 },
  { r: 2.0, a: 0.05, d: 0.3 },
  { r: 5.93, a: 0.2, d: 0.1 },
  { r: 12.1, a: 0.05, d: 0.035 },
];

/** Kalimba: warm sine tine, quick inharmonic overtone "ping" and a soft tine click. */
export function kalimba(s: Synth, t: number, midi: number, vel: number, o: NoteOpts = {}): void {
  const f = mtof(midi);
  const decay = (o.decay ?? 1) * clamp(2.3 * Math.pow(440 / f, 0.4), 0.7, 3.2);
  const { v } = strike(s, t, f, 0.3 * vel, decay, KALIMBA, o, {
    attack: 0.0015,
    lp: o.muted ? 1500 : 6500 + f * 2,
  });
  if (!o.muted) transient(v, s, t, 'bandpass', 2600 + f * 1.5, 1.4, 0.035 * vel, 0.01);
}

const GLASS: readonly Part[] = [
  { r: 1, a: 1, d: 1 },
  { r: 1.0018, a: 0.5, d: 0.85 }, // slow 3-cent beating = shimmer
  { r: 2.76, a: 0.38, d: 0.42 },
  { r: 5.4, a: 0.17, d: 0.2 },
  { r: 8.93, a: 0.07, d: 0.1 },
];

/** Glass bell / celesta: 1 : 2.76 : 5.4 : 8.93 partials, long airy ring. */
export function glassBell(s: Synth, t: number, midi: number, vel: number, o: NoteOpts = {}): void {
  const f = mtof(midi);
  const decay = (o.decay ?? 1) * clamp(3.2 * Math.pow(700 / f, 0.3), 1.0, 4.5);
  strike(s, t, f, 0.16 * vel, decay, GLASS, o, { attack: 0.002, lp: o.muted ? 2500 : 12000 });
}

/** A quick sprinkle of tiny high sine pings fanning across the stereo field. */
export function twinkle(s: Synth, t: number, vel: number, notes: readonly number[], o: NoteOpts = {}): void {
  const v = s.pool.voice(t, s.route, { send: o.send ?? 0.45 });
  const ny = nyquist(s.kit.ctx);
  const n = notes.length;
  const rng = s.kit.rng;
  for (let i = 0; i < n; i++) {
    const m = notes[i];
    if (m === undefined) continue;
    const tt = t + i * 0.042 + rng.next() * 0.012;
    const f = Math.min(mtof(m), 3800, ny);
    const osc = v.osc('sine', f);
    const g = v.gain();
    const p = v.panner(n > 1 ? (i / (n - 1)) * 1.2 - 0.6 : 0);
    link(osc, g, p, v.out);
    const end = perc(g.gain, tt, 0.05 * vel * (1 - i * 0.08), 0.001, 0.4);
    v.play(osc, tt, end);
  }
}

const GLASS_TICK: readonly Part[] = [
  { r: 1, a: 1, d: 1 },
  { r: 2.76, a: 0.35, d: 0.35 },
];

/** UI glass tick (muted = lower, darker "back" tick). */
export function glassTick(s: Synth, t: number, midi: number, vel: number, o: NoteOpts = {}): void {
  const f = mtof(midi);
  strike(s, t, f, 0.12 * vel, o.muted ? 0.08 : 0.17, GLASS_TICK, o, {
    attack: 0.001,
    lp: o.muted ? 1400 : 12000,
  });
}

const HANDPAN: readonly Part[] = [
  { r: 1, a: 1, d: 1 },
  { r: 2, a: 0.3, d: 0.55 },
  { r: 2.006, a: 0.12, d: 0.5 },
  { r: 3, a: 0.08, d: 0.25 },
];

/** Handpan / hang drum: sine + octave + compound fifth, slight pitch settle, soft hand "tok". */
export function handpan(s: Synth, t: number, midi: number, vel: number, o: NoteOpts = {}): void {
  const f = mtof(midi);
  const decay = (o.decay ?? 1) * clamp(3.0 * Math.pow(300 / f, 0.25), 1.2, 3.6);
  const { v } = strike(s, t, f, 0.26 * vel, decay, HANDPAN, { ...o, send: o.send ?? 0.3 }, {
    attack: 0.005,
    lp: o.muted ? 1100 : 3400 + f,
    drop: 1.01,
    dropTime: 0.08,
  });
  transient(v, s, t, 'lowpass', 320, 0.7, 0.12 * vel, 0.05, s.kit.noise.brown);
}

const ZILL: readonly Part[] = [
  { r: 1, a: 1, d: 1 },
  { r: 1.0063, a: 0.8, d: 0.9 },
  { r: 1.512, a: 0.55, d: 0.7 },
  { r: 2.17, a: 0.4, d: 0.5 },
  { r: 2.94, a: 0.22, d: 0.35 },
  { r: 3.81, a: 0.12, d: 0.25 },
];

export interface ZillOpts extends NoteOpts {
  /** Pitch multiplier around ~2.5 kHz. */
  readonly pitch?: number;
}

/** Zills (finger cymbals): beating inharmonic partials + a breath of hiss. */
export function zill(s: Synth, t: number, vel: number, o: ZillOpts = {}): void {
  const f = 2480 * (o.pitch ?? 1);
  const { v } = strike(s, t, f, 0.035 * vel, 1.6 * (o.decay ?? 1), ZILL, { ...o, send: o.send ?? 0.4 }, {
    attack: 0.0008,
    lp: 14000,
  });
  transient(v, s, t, 'highpass', 7000, 0.7, 0.03 * vel, 0.03);
}

const WOOD: readonly Part[] = [
  { r: 1, a: 1, d: 1 },
  { r: 2.68, a: 0.25, d: 0.4 },
];

/** Hollow wood tick (dunes UI). */
export function woodTick(s: Synth, t: number, midi: number, vel: number, o: NoteOpts = {}): void {
  const f = mtof(midi);
  const { v } = strike(s, t, f, 0.16 * vel, o.muted ? 0.08 : 0.12, WOOD, o, {
    attack: 0.001,
    lp: o.muted ? 900 : 5000,
    drop: 1.06,
    dropTime: 0.012,
  });
  transient(v, s, t, 'bandpass', f * 2.2, 3, 0.05 * vel, 0.008);
}
